'use client';

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import {
  usePortalContainerOverride,
  usePortalReplacementRef,
} from '@/components/ui/portal-container';
import {
  TooltipA11yRoot,
  useTooltipPopupA11y,
  useTooltipTriggerA11y,
} from '@/components/ui/tooltip';
import { cn, extractText } from '@/lib/utils';

const tooltipVariants = cva(
  'motion-safe:transition-opacity motion-safe:duration-(--duration-fast) motion-safe:ease-standard motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0 data-[instant=delay]:transition-none data-[instant=delay]:duration-0 data-[instant=delay]:data-starting-style:opacity-100 data-[instant=delay]:data-ending-style:opacity-100 z-50 w-fit max-w-xs origin-(--transform-origin) px-3 py-1.5 text-xs shadow-[var(--shadow-tooltip)]',
  {
    variants: {
      variant: {
        primary: 'bg-foreground text-background',
        secondary:
          'bg-secondary text-secondary-foreground border border-border shadow-sm',
        info: 'bg-info text-info-foreground',
        success: 'bg-success text-success-foreground',
        warning: 'bg-warning text-warning-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
      },
      shape: {
        default: 'rounded-md',
        rounded: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      shape: 'default',
    },
  },
);

const arrowVariants = cva(
  'size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] z-50 data-[side=bottom]:top-1 data-[side=left]:top-1/2! data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2! data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=top]:-bottom-2.5',
  {
    variants: {
      variant: {
        primary: 'bg-foreground fill-foreground',
        secondary: 'bg-secondary fill-secondary',
        info: 'bg-info fill-info',
        success: 'bg-success fill-success',
        warning: 'bg-warning fill-warning',
        destructive: 'bg-destructive fill-destructive',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

type TooltipVariant =
  | 'primary'
  | 'secondary'
  | 'info'
  | 'success'
  | 'warning'
  | 'destructive';
type TooltipShape = 'default' | 'rounded';
type TooltipPosition = 'top' | 'bottom' | 'left' | 'right' | 'none';

// =============================================================================
// Base Tooltip Sub-components
// =============================================================================

function TooltipProvider({
  delay = 0,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      {...props}
    />
  );
}

function TooltipRoot(props: TooltipPrimitive.Root.Props) {
  return <TooltipA11yRoot data-slot="tooltip" {...props} />;
}

const TooltipTrigger = React.forwardRef<
  HTMLButtonElement,
  TooltipPrimitive.Trigger.Props
>(function TooltipTrigger(
  { 'aria-describedby': describedBy, render, ...props },
  ref,
) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
      {...useTooltipTriggerA11y(
        { 'aria-describedby': describedBy, render },
        ref,
      )}
    />
  );
});

interface TooltipPopupProps
  extends TooltipPrimitive.Popup.Props,
    Pick<
      TooltipPrimitive.Positioner.Props,
      'align' | 'alignOffset' | 'side' | 'sideOffset'
    > {
  variant?: TooltipVariant;
  shape?: TooltipShape;
  /** Optional Base UI portal container for the tooltip popup. */
  container?: TooltipPrimitive.Portal.Props['container'];
  /** When true or position is 'none', hides the arrow */
  hideArrow?: boolean;
}

function TooltipPopup({
  className,
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  variant = 'primary',
  shape = 'default',
  hideArrow = false,
  container,
  children,
  ...props
}: TooltipPopupProps) {
  const resolvedContainer = usePortalContainerOverride(container);
  const { ownerDocumentBody, ...a11y } = useTooltipPopupA11y(
    props.id,
    props.hidden,
  );
  const portalContainer =
    resolvedContainer !== undefined ? resolvedContainer : ownerDocumentBody;
  const { ref: popupRef } = usePortalReplacementRef(portalContainer, props.ref);
  return (
    <TooltipPrimitive.Portal container={portalContainer}>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-popup"
          className={cn(tooltipVariants({ variant, shape }), className)}
          {...props}
          {...a11y}
          ref={popupRef}
        >
          {children}
          {!hideArrow && (
            <TooltipPrimitive.Arrow className={arrowVariants({ variant })} />
          )}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

// =============================================================================
// Rich Content Sub-components
// =============================================================================

function TooltipIcon({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="tooltip-icon"
      className={cn('shrink-0 [&>svg]:size-4', className)}
    >
      {children}
    </span>
  );
}
TooltipIcon.displayName = 'Tooltip.Icon';

function TooltipAvatar({
  src,
  alt,
  children,
  className,
}: {
  src?: string;
  alt: string;
  children?: React.ReactNode;
  className?: string;
}) {
  if (children) {
    return (
      <span data-slot="tooltip-avatar" className={cn('shrink-0', className)}>
        {children}
      </span>
    );
  }
  return (
    <img
      data-slot="tooltip-avatar"
      src={src}
      alt={alt}
      className={cn('size-4 shrink-0 rounded-full object-cover', className)}
    />
  );
}
TooltipAvatar.displayName = 'Tooltip.Avatar';

function TooltipContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="tooltip-content"
      className={cn('flex flex-col gap-0.5', className)}
    >
      {children}
    </div>
  );
}
TooltipContent.displayName = 'Tooltip.Content';

function TooltipTitle({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      data-slot="tooltip-title"
      className={cn('text-xs font-semibold leading-4', className)}
    >
      {children}
    </span>
  );
}
TooltipTitle.displayName = 'Tooltip.Title';

interface TooltipSubtitleProps {
  children: React.ReactNode;
  className?: string;
  /** Variant affects subtitle opacity */
  variant?: TooltipVariant;
}

function TooltipSubtitle({
  children,
  className,
  variant = 'primary',
}: TooltipSubtitleProps) {
  const subtitleClass =
    variant === 'secondary'
      ? 'text-muted-foreground'
      : 'text-inherit opacity-70';

  return (
    <span
      data-slot="tooltip-subtitle"
      className={cn('text-xs font-medium leading-4', subtitleClass, className)}
    >
      {children}
    </span>
  );
}
TooltipSubtitle.displayName = 'Tooltip.Subtitle';

function TooltipBadge({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span data-slot="tooltip-badge" className={cn('shrink-0', className)}>
      {children}
    </span>
  );
}
TooltipBadge.displayName = 'Tooltip.Badge';

interface TooltipImageProps {
  src: string;
  alt: string;
  layout?: 'vertical' | 'horizontal';
  className?: string;
}

function TooltipImage({
  src,
  alt,
  layout = 'vertical',
  className,
}: TooltipImageProps) {
  return (
    <img
      data-slot="tooltip-image"
      src={src}
      alt={alt}
      className={cn(
        'rounded-md object-cover',
        layout === 'vertical' ? 'w-full h-20' : 'w-20 h-14 shrink-0',
        className,
      )}
    />
  );
}
TooltipImage.displayName = 'Tooltip.Image';

// =============================================================================
// High-level Tooltip Wrapper Component
// =============================================================================

interface TooltipProps {
  /** The element that triggers the tooltip (required) */
  trigger: React.ReactElement;
  /** The main tooltip text */
  content: React.ReactNode;
  /** Style variant */
  variant?: TooltipVariant;
  /** Shape variant */
  shape?: TooltipShape;
  /** Arrow position - 'none' hides the arrow */
  position?: TooltipPosition;
  /** Alignment of tooltip relative to trigger */
  align?: 'start' | 'center' | 'end';
  /** Delay in ms before showing tooltip */
  delayDuration?: number;
  /** Distance from trigger */
  sideOffset?: number;
  /** Optional icon before content */
  icon?: React.ReactNode;
  /** Optional avatar - string for src or ReactNode for custom */
  avatar?: string | React.ReactNode;
  /** Optional supporting text */
  subtitle?: React.ReactNode;
  /** Optional badge element */
  badge?: React.ReactNode;
  /** Optional image */
  image?: string;
  /** Image layout - auto-detected or override */
  imageLayout?: 'vertical' | 'horizontal';
  /** Optional Base UI portal container. Takes precedence over the trigger owner document. */
  container?: TooltipPrimitive.Portal.Props['container'];
  /** Override content with custom children */
  children?: React.ReactNode;
}

interface TooltipRenderedContentProps {
  children?: React.ReactNode;
  content: React.ReactNode;
  variant: TooltipVariant;
  icon?: React.ReactNode;
  avatar?: string | React.ReactNode;
  subtitle?: React.ReactNode;
  badge?: React.ReactNode;
  image?: string;
  imageLayout: 'vertical' | 'horizontal';
  hasRichContent: React.ReactNode;
  contentAlt: string;
}

function TooltipRenderedContent({
  children,
  content,
  variant,
  icon,
  avatar,
  subtitle,
  badge,
  image,
  imageLayout,
  hasRichContent,
  contentAlt,
}: TooltipRenderedContentProps) {
  if (children) return children;

  const subtitleElement = subtitle && (
    <TooltipSubtitle variant={variant}>{subtitle}</TooltipSubtitle>
  );

  if (image) {
    return (
      <div
        className={cn(
          'flex gap-1.5',
          imageLayout === 'vertical' ? 'flex-col' : 'flex-row items-center',
        )}
      >
        <TooltipImage src={image} alt={contentAlt} layout={imageLayout} />
        <TooltipContent>
          <TooltipTitle>{content}</TooltipTitle>
          {subtitleElement}
        </TooltipContent>
      </div>
    );
  }

  if (!hasRichContent) return content;

  return (
    <div className="flex items-center gap-1.5">
      {avatar &&
        (typeof avatar === 'string' ? (
          <TooltipAvatar src={avatar} alt={contentAlt} />
        ) : (
          <TooltipAvatar alt={contentAlt}>{avatar}</TooltipAvatar>
        ))}
      {icon && <TooltipIcon>{icon}</TooltipIcon>}
      <TooltipContent>
        <TooltipTitle>{content}</TooltipTitle>
        {subtitleElement}
      </TooltipContent>
      {badge && <TooltipBadge>{badge}</TooltipBadge>}
    </div>
  );
}

const Tooltip = ({
  trigger,
  content,
  variant = 'primary',
  shape = 'default',
  position = 'top',
  align = 'center',
  delayDuration,
  sideOffset = 6,
  icon,
  avatar,
  subtitle,
  badge,
  image,
  imageLayout,
  container,
  children,
}: TooltipProps) => {
  // Determine if we have rich content
  const hasRichContent = icon || avatar || subtitle || badge || image;

  const resolvedImageLayout = imageLayout ?? 'vertical';
  const contentAlt = extractText(content);

  return (
    <TooltipProvider delay={delayDuration}>
      <TooltipRoot>
        <TooltipTrigger render={trigger} />
        <TooltipPopup
          container={container}
          side={position === 'none' ? 'top' : position}
          align={align}
          sideOffset={sideOffset}
          variant={variant}
          shape={shape}
          hideArrow={position === 'none'}
          className={cn(hasRichContent && 'p-2')}
        >
          <TooltipRenderedContent
            content={content}
            variant={variant}
            icon={icon}
            avatar={avatar}
            subtitle={subtitle}
            badge={badge}
            image={image}
            imageLayout={resolvedImageLayout}
            hasRichContent={hasRichContent}
            contentAlt={contentAlt}
          >
            {children}
          </TooltipRenderedContent>
        </TooltipPopup>
      </TooltipRoot>
    </TooltipProvider>
  );
};

Tooltip.displayName = 'Tooltip';
TooltipProvider.displayName = 'Tooltip.Provider';
TooltipRoot.displayName = 'Tooltip.Root';
TooltipTrigger.displayName = 'Tooltip.Trigger';
TooltipPopup.displayName = 'Tooltip.Popup';

/**
 * Tooltip component with dot notation for compound parts.
 *
 * @example
 * ```tsx
 * // Simple usage (recommended for most cases)
 * <Tooltip trigger={<Button>Hover me</Button>} content="Tooltip text" />
 *
 * // With variants
 * <Tooltip trigger={<Button>Save</Button>} content="Saved!" variant="success" shape="rounded" />
 *
 * // With rich content
 * <Tooltip trigger={<Button>Info</Button>} content="Title" icon={<InfoIcon />} subtitle="Description" />
 *
 * // Advanced usage with dot notation
 * <Tooltip.Root>
 *   <Tooltip.Trigger render={<Button>Hover me</Button>} />
 *   <Tooltip.Popup variant="info">
 *     <Tooltip.Icon><InfoIcon /></Tooltip.Icon>
 *     <Tooltip.Content>
 *       <Tooltip.Title>Title</Tooltip.Title>
 *       <Tooltip.Subtitle>Description</Tooltip.Subtitle>
 *     </Tooltip.Content>
 *   </Tooltip.Popup>
 * </Tooltip.Root>
 * ```
 */
const TooltipCompound = Object.assign(Tooltip, {
  Root: TooltipRoot,
  Provider: TooltipProvider,
  Trigger: TooltipTrigger,
  Popup: TooltipPopup,
  Icon: TooltipIcon,
  Avatar: TooltipAvatar,
  Content: TooltipContent,
  Title: TooltipTitle,
  Subtitle: TooltipSubtitle,
  Badge: TooltipBadge,
  Image: TooltipImage,
});

export type { TooltipPosition, TooltipProps, TooltipShape, TooltipVariant };
export {
  arrowVariants,
  TooltipAvatar,
  TooltipBadge,
  TooltipCompound as Tooltip,
  TooltipContent,
  TooltipIcon,
  TooltipImage,
  TooltipPopup,
  TooltipProvider,
  TooltipRoot,
  TooltipSubtitle,
  TooltipTitle,
  TooltipTrigger,
  tooltipVariants,
};
